/**
 * Weather Legend Context
 * Manages which weather layers' legends are currently displayed.
 * Supports multiple simultaneous legends for ABB weather layers.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { timeSince, convertUnit, Units } from '@ais-ui/utils';
import { useDateTimeFormat } from '../../conversions/useDateFormat';
import type { WeatherLegend } from './types';
import { useABBLayerMetadata } from '../LayerControl/Actions/useABBWeatherLayers';
import type { ABBLegend } from '../LayerControl/Actions/useABBWeatherLayers';
import { useUnitSystem } from '../../Units/useUnitSystem';

export interface ActiveLegendEntry {
  layerId: string;
  label: string;
}

interface WeatherLegendContextValue {
  activeLegends: ActiveLegendEntry[];
  timepointLabels: Record<string, string>;
  toggleLegend: (layerId: string, layerLabel: string) => void;
  showLegend: (layerId: string, layerLabel: string) => void;
  isLegendActiveForLayer: (layerId: string) => boolean;
  dismissLegendForLayer: (layerId: string) => void;
  setTimepointLabel: (layerId: string, label: string | null) => void;
}

const WeatherLegendContext = createContext<WeatherLegendContextValue | null>(
  null,
);

interface WeatherLegendProviderProps {
  children: React.ReactNode;
}

export const WeatherLegendProvider: React.FC<WeatherLegendProviderProps> = ({
  children,
}) => {
  const [activeLegends, setActiveLegends] = useState<ActiveLegendEntry[]>([]);
  const [timepointLabels, setTimepointLabels] = useState<
    Record<string, string>
  >({});

  const toggleLegend = useCallback((layerId: string, layerLabel: string) => {
    setActiveLegends((prev) => {
      const exists = prev.some((l) => l.layerId === layerId);
      if (exists) {
        return prev.filter((l) => l.layerId !== layerId);
      }
      return [...prev, { layerId, label: layerLabel }];
    });
  }, []);

  const showLegend = useCallback((layerId: string, layerLabel: string) => {
    setActiveLegends((prev) => {
      const exists = prev.some((l) => l.layerId === layerId);
      if (exists) return prev;
      return [...prev, { layerId, label: layerLabel }];
    });
  }, []);

  const isLegendActiveForLayer = useCallback(
    (layerId: string) => activeLegends.some((l) => l.layerId === layerId),
    [activeLegends],
  );

  const dismissLegendForLayer = useCallback((layerId: string) => {
    setActiveLegends((prev) => prev.filter((l) => l.layerId !== layerId));
    setTimepointLabels((prev) => {
      const next = { ...prev };
      delete next[layerId];
      return next;
    });
  }, []);

  const setTimepointLabel = useCallback(
    (layerId: string, label: string | null) => {
      setTimepointLabels((prev) => {
        if (label === null) {
          const next = { ...prev };
          delete next[layerId];
          return next;
        }
        if (prev[layerId] === label) return prev;
        return { ...prev, [layerId]: label };
      });
    },
    [],
  );

  const value: WeatherLegendContextValue = {
    activeLegends,
    timepointLabels,
    toggleLegend,
    showLegend,
    isLegendActiveForLayer,
    dismissLegendForLayer,
    setTimepointLabel,
  };

  return (
    <WeatherLegendContext.Provider value={value}>
      {children}
    </WeatherLegendContext.Provider>
  );
};

export const useWeatherLegend = (): WeatherLegendContextValue => {
  const context = useContext(WeatherLegendContext);
  if (!context) {
    throw new Error(
      'useWeatherLegend must be used within a WeatherLegendProvider',
    );
  }
  return context;
};

// ==================== Per-layer legend hook ====================

/** Human-readable interval string from seconds, e.g. "every 3 h" or "every 30 min". */
function formatInterval(seconds: number | undefined): string {
  if (!seconds || seconds <= 0) return '';
  const hours = seconds / 3600;
  if (hours >= 1) {
    return `every ${hours % 1 === 0 ? hours : hours.toFixed(1)} h`;
  }
  return `every ${Math.round(seconds / 60)} min`;
}

/** Convert ABB legend data to WeatherLegend shape for the shared renderer. */
function toUnifiedLegend(legends: ABBLegend[]): WeatherLegend[] {
  return legends.map((l) => ({
    label: l.label,
    unit: l.unit === 'N/A' ? undefined : l.unit,
    colorTableType: l.colorTableType,
    colorTable: l.colorTable.map((c) => ({
      color: c.color,
      value: c.value === -1 ? null : c.value,
      category: c.category || null,
    })),
  }));
}

/** Apply unit conversion to legend values and unit labels. */
function convertLegendUnits(
  legends: WeatherLegend[],
  targetSystem: Units,
): WeatherLegend[] {
  return legends.map((legend) => {
    if (!legend.unit) return legend;

    // Check if conversion is needed for this unit
    const probe = convertUnit(1, legend.unit, targetSystem);
    if (!probe) return legend;

    return {
      ...legend,
      unit: probe.unit,
      colorTable: legend.colorTable.map((item) => {
        if (item.value == null) return item;
        const converted = convertUnit(item.value, legend.unit!, targetSystem);
        return converted ? { ...item, value: converted.value } : item;
      }),
    };
  });
}

export interface ResolvedWeatherLegend {
  legend: WeatherLegend[] | null;
  subtitle: string | null;
  /** Unit string from metadata (e.g. "m/s") — useful for value-grid layers that have no color table. */
  unit: string | null;
  isLoading: boolean;
}

/**
 * Hook to resolve legend data for a single active weather layer.
 * Use this in per-legend section components.
 */
export function useResolvedWeatherLegend(
  entry: ActiveLegendEntry,
  timepointLabel?: string,
): ResolvedWeatherLegend {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const unitSystem = useUnitSystem();

  // ABB metadata
  const { data: abbMetadata, isLoading } = useABBLayerMetadata(entry.layerId);

  const legend: WeatherLegend[] | null = useMemo(() => {
    if (!abbMetadata?.legends) return null;
    const raw = toUnifiedLegend(abbMetadata.legends);
    return convertLegendUnits(raw, unitSystem);
  }, [abbMetadata, unitSystem]);

  const subtitle = useMemo(() => {
    const intervalStr = formatInterval(abbMetadata?.timepointintervals);
    const suffix = intervalStr ? ` · ${intervalStr}` : '';

    if (timepointLabel) {
      return `${timepointLabel}${suffix}`;
    }
    if (abbMetadata?.timepoints) {
      const tps = Object.values(abbMetadata.timepoints);
      if (tps.length === 0) return null;
      const sorted = [...tps].sort((a, b) => a.timestamp - b.timestamp);
      const latest = new Date(sorted[sorted.length - 1].timepoint);
      const absolute = formatDateTimeWithTimeZone(latest);

      if (latest.getTime() <= Date.now()) {
        return `Latest ${timeSince(latest)} (${absolute})${suffix}`;
      }
      return `Forecast to ${absolute}${suffix}`;
    }

    return null;
  }, [abbMetadata, timepointLabel, formatDateTimeWithTimeZone]);

  // Extract the primary unit from metadata (for value-grid layers without color tables)
  const unit = useMemo(() => {
    const firstUnit = abbMetadata?.legends?.[0]?.unit;
    if (!firstUnit || firstUnit === 'N/A') return null;
    return firstUnit;
  }, [abbMetadata]);

  return { legend, subtitle, unit, isLoading };
}

export default WeatherLegendContext;
